import * as THREE from "three";

import woodOak from "../../../../assets/textures/wood-oak.jpg";
import woodDark from "../../../../assets/textures/wood-dark.jpg";
import concrete from "../../../../assets/textures/concrete.jpg";
import plasterWhite from "../../../../assets/textures/plaster-white.jpg";
import metal from "../../../../assets/textures/metal.jpg";

/**
 * Набор тестовых текстур (CC0, Poly Haven).
 * Для UI выбора / превью.
 */
export const TEST_TEXTURES = [
    {id: "wood-oak", name: "Дуб светлый", url: woodOak},
    {id: "wood-dark", name: "Дерево тёмное", url: woodDark},
    // Мелкое зерно на 56px сглаживается в «серую заливку» — zoom только для превью в UI.
    {id: "concrete", name: "Бетон", url: concrete, previewZoom: 3},
    {id: "plaster-white", name: "Штукатурка светлая", url: plasterWhite, previewZoom: 2},
    {id: "metal", name: "Металл", url: metal},
];

export const DEFAULT_TEXTURE_URL = woodOak;

/** Префикс id пользовательских текстур (не из каталога). */
export const CUSTOM_TEXTURE_ID_PREFIX = "custom-";

export const isCustomTextureId = (id) =>
    typeof id === "string" && id.startsWith(CUSTOM_TEXTURE_ID_PREFIX);

/** Макс. сторона при загрузке своей текстуры (чтобы не раздувать localStorage). */
export const CUSTOM_TEXTURE_MAX_SIDE = 1024;

/** Запись каталога из встроенного ассета. */
export const builtinTextureToCatalogEntry = (tex) => ({
    id: tex.id,
    name: tex.name,
    url: tex.url,
    sourceUrl: tex.url,
    previewZoom: tex.previewZoom ?? 1,
    builtin: true,
});

export const createDefaultTextureCatalog = () =>
    TEST_TEXTURES.map(builtinTextureToCatalogEntry);

/**
 * Загрузить любой URL картинки (в т.ч. webpack asset) → data URL для модалки.
 */
export const loadImageUrlAsDataUrl = (url, maxSide = CUSTOM_TEXTURE_MAX_SIDE) =>
    new Promise((resolve, reject) => {
        if (!url) {
            reject(new Error("Нет изображения"));
            return;
        }
        if (String(url).startsWith("data:")) {
            resolve(url);
            return;
        }
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onerror = () => reject(new Error("Не удалось загрузить изображение для правки"));
        img.onload = () => {
            let {width, height} = img;
            if (width > maxSide || height > maxSide) {
                const scale = maxSide / Math.max(width, height);
                width = Math.max(1, Math.round(width * scale));
                height = Math.max(1, Math.round(height * scale));
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new Error("Canvas недоступен"));
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", 0.92));
        };
        img.src = url;
    });

/** мм на один повтор картинки (масштаб декора). */
export const DEFAULT_TEXTURE_SCALE_MM = 500;

/** Поворот рисунка на пласти, ° (0 / 90 / 180 / 270). */
export const DEFAULT_TEXTURE_ROTATION_DEG = 0;

export const normalizeTextureRotationDeg = (deg) => {
    const n = Math.round((Number(deg) || 0) / 90) * 90;
    return ((n % 360) + 360) % 360;
};

export const clampTextureScaleMm = (mm) => {
    const n = Number(mm);
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_TEXTURE_SCALE_MM;
    return Math.min(2000, Math.max(100, n));
};

/**
 * Локальный каталог по id/имени материала организации.
 * Ключ: String(materialOptionOrganizationId) или точное имя.
 */
export const LOCAL_TEXTURE_BY_MATERIAL_KEY = {
    // Пример: "123": woodOak,
};

/** Подбор по словам в названии материала (для теста без id). */
const NAME_HINTS = [
    {re: /дуб|oak|дерев|wood|лдсп|лдсп/i, url: woodOak},
    {re: /венге|тёмн|темн|орех|wenge/i, url: woodDark},
    {re: /бетон|concrete|цемент/i, url: concrete},
    {re: /бел|white|альп|фарфор|кашемир|серы/i, url: plasterWhite},
    {re: /металл|metal|хром|сталь/i, url: metal},
];

/**
 * URL текстуры для детали.
 * Приоритет: textureUrl с бэка → каталог по id → эвристика по имени → дефолт.
 */
export const resolveDetailTextureUrl = (detailData) => {
    const fromApi =
        detailData?.textureUrl
        || detailData?.material?.textureUrl
        || detailData?.materialOptionOrganization?.textureUrl
        || null;

    if (fromApi) {
        return fromApi;
    }

    const keys = [
        detailData?.materialOptionOrganizationId,
        detailData?.materialId,
        detailData?.materialOptionOrganizationName,
        detailData?.materialName,
    ]
        .filter((k) => k != null && k !== "")
        .map(String);

    for (const key of keys) {
        if (LOCAL_TEXTURE_BY_MATERIAL_KEY[key]) {
            return LOCAL_TEXTURE_BY_MATERIAL_KEY[key];
        }
    }

    const nameBlob = keys.join(" ");
    for (const hint of NAME_HINTS) {
        if (hint.re.test(nameBlob)) {
            return hint.url;
        }
    }

    return DEFAULT_TEXTURE_URL;
};

/**
 * Прочитать файл → исходник для модалки (ещё без правок цвета).
 * @returns {Promise<{id: string, suggestedName: string, sourceUrl: string, width: number, height: number}>}
 */
export const readImageFileAsUploadSource = (file) =>
    new Promise((resolve, reject) => {
        if (!file || !String(file.type || "").startsWith("image/")) {
            reject(new Error("Выберите файл изображения (JPG, PNG, WebP…)"));
            return;
        }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error("Файл не является изображением"));
            img.onload = () => {
                const max = CUSTOM_TEXTURE_MAX_SIDE;
                let {width, height} = img;
                if (width > max || height > max) {
                    const scale = max / Math.max(width, height);
                    width = Math.max(1, Math.round(width * scale));
                    height = Math.max(1, Math.round(height * scale));
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Canvas недоступен"));
                    return;
                }
                ctx.drawImage(img, 0, 0, width, height);
                const sourceUrl = canvas.toDataURL("image/jpeg", 0.92);
                const suggestedName = String(file.name || "текстура")
                    .replace(/\.[^.]+$/, "")
                    .trim()
                    .slice(0, 40) || "Своя";
                resolve({
                    id: `${CUSTOM_TEXTURE_ID_PREFIX}${Date.now().toString(36)}-${Math.random()
                        .toString(16)
                        .slice(2, 8)}`,
                    suggestedName,
                    sourceUrl,
                    width,
                    height,
                });
            };
            img.src = String(reader.result || "");
        };
        reader.readAsDataURL(file);
    });

const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return {h: 0, s: 0, l};
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    switch (max) {
        case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
        case g:
            h = ((b - r) / d + 2) / 6;
            break;
        default:
            h = ((r - g) / d + 4) / 6;
            break;
    }
    return {h, s, l};
};

const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
};

const hslToRgb = (h, s, l) => {
    if (s === 0) {
        const v = Math.round(l * 255);
        return {r: v, g: v, b: v};
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hue2rgb(p, q, h) * 255),
        b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
};

export const DEFAULT_TEXTURE_IMAGE_ADJUST = {
    brightness: 1,
    contrast: 1,
    tintColor: "#c4a574",
    tintAmount: 0,
};

export const clampTexturePreviewZoom = (zoom) => {
    const n = Number(zoom);
    if (!Number.isFinite(n)) return 1;
    return Math.min(4, Math.max(1, Math.round(n * 10) / 10));
};

/** Произвольный угол рисунка, ° → [0, 360). */
export const wrapTextureRotationDeg = (deg) => {
    const n = Number(deg);
    if (!Number.isFinite(n)) return 0;
    return ((n % 360) + 360) % 360;
};

const parseHexColor = (hex) => {
    const raw = String(hex || "").replace("#", "").trim();
    if (raw.length === 3) {
        const r = parseInt(raw[0] + raw[0], 16);
        const g = parseInt(raw[1] + raw[1], 16);
        const b = parseInt(raw[2] + raw[2], 16);
        if ([r, g, b].every(Number.isFinite)) return {r, g, b};
    }
    if (raw.length === 6) {
        const r = parseInt(raw.slice(0, 2), 16);
        const g = parseInt(raw.slice(2, 4), 16);
        const b = parseInt(raw.slice(4, 6), 16);
        if ([r, g, b].every(Number.isFinite)) return {r, g, b};
    }
    return {r: 196, g: 165, b: 116};
};

/**
 * Зум + поворот картинки, затем светлота/контраст/тонирование цветом.
 * @returns {Promise<string>} data URL JPEG
 */
export const renderAdjustedTextureUrl = (sourceUrl, adjust = {}, options = {}) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = () => reject(new Error("Не удалось обработать изображение"));
        img.onload = () => {
            const maxSide = options.maxSide ?? CUSTOM_TEXTURE_MAX_SIDE;
            const outSize = Math.min(maxSide, Math.max(img.width, img.height, 1));
            const canvas = document.createElement("canvas");
            canvas.width = outSize;
            canvas.height = outSize;
            const ctx = canvas.getContext("2d", {willReadFrequently: true});
            if (!ctx) {
                reject(new Error("Canvas недоступен"));
                return;
            }

            const imageZoom = clampTexturePreviewZoom(adjust.imageZoom ?? 1);
            const imageRotationDeg = wrapTextureRotationDeg(adjust.imageRotationDeg ?? 0);

            ctx.fillStyle = "#808080";
            ctx.fillRect(0, 0, outSize, outSize);
            ctx.save();
            ctx.translate(outSize / 2, outSize / 2);
            ctx.rotate((imageRotationDeg * Math.PI) / 180);
            ctx.scale(imageZoom, imageZoom);
            // cover square
            const scale = Math.max(outSize / img.width, outSize / img.height);
            const dw = img.width * scale;
            const dh = img.height * scale;
            ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
            ctx.restore();

            const brightness = Number(adjust.brightness);
            const contrast = Number(adjust.contrast);
            const tintAmount = Number(adjust.tintAmount);
            const bMul = Number.isFinite(brightness) ? brightness : 1;
            const cMul = Number.isFinite(contrast) ? contrast : 1;
            const tAmt = Number.isFinite(tintAmount) ? Math.min(1, Math.max(0, tintAmount)) : 0;
            const tint = parseHexColor(adjust.tintColor);

            const identity =
                Math.abs(bMul - 1) < 0.001
                && Math.abs(cMul - 1) < 0.001
                && tAmt < 0.001;

            if (!identity) {
                const imageData = ctx.getImageData(0, 0, outSize, outSize);
                const data = imageData.data;
                const tintHsl = rgbToHsl(tint.r, tint.g, tint.b);
                for (let i = 0; i < data.length; i += 4) {
                    let r = data[i];
                    let g = data[i + 1];
                    let b = data[i + 2];

                    r *= bMul;
                    g *= bMul;
                    b *= bMul;

                    r = (r - 128) * cMul + 128;
                    g = (g - 128) * cMul + 128;
                    b = (b - 128) * cMul + 128;

                    r = Math.min(255, Math.max(0, r));
                    g = Math.min(255, Math.max(0, g));
                    b = Math.min(255, Math.max(0, b));

                    if (tAmt > 0.001) {
                        const hsl = rgbToHsl(r, g, b);
                        const mixed = hslToRgb(
                            tintHsl.h,
                            Math.min(1, Math.max(0, tintHsl.s * 0.85 + hsl.s * 0.15)),
                            hsl.l,
                        );
                        r = r * (1 - tAmt) + mixed.r * tAmt;
                        g = g * (1 - tAmt) + mixed.g * tAmt;
                        b = b * (1 - tAmt) + mixed.b * tAmt;
                    }

                    data[i] = Math.min(255, Math.max(0, Math.round(r)));
                    data[i + 1] = Math.min(255, Math.max(0, Math.round(g)));
                    data[i + 2] = Math.min(255, Math.max(0, Math.round(b)));
                }
                ctx.putImageData(imageData, 0, 0);
            }

            resolve(canvas.toDataURL("image/jpeg", options.quality ?? 0.88));
        };
        img.src = sourceUrl;
    });

/** Собрать запись каталога своей текстуры (с исходником для повторного редактирования). */
export const buildCustomTextureRecord = ({
    id,
    name,
    url,
    sourceUrl,
    defaultScaleMm = DEFAULT_TEXTURE_SCALE_MM,
    defaultRotationDeg = DEFAULT_TEXTURE_ROTATION_DEG,
    previewZoom = 1,
    imageZoom = 1,
    imageRotationDeg = 0,
    brightness = DEFAULT_TEXTURE_IMAGE_ADJUST.brightness,
    contrast = DEFAULT_TEXTURE_IMAGE_ADJUST.contrast,
    tintColor = DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor,
    tintAmount = DEFAULT_TEXTURE_IMAGE_ADJUST.tintAmount,
}) => ({
    id,
    name: String(name || "Своя").trim().slice(0, 40) || "Своя",
    url,
    sourceUrl: sourceUrl || url,
    defaultScaleMm: clampTextureScaleMm(defaultScaleMm),
    defaultRotationDeg: wrapTextureRotationDeg(defaultRotationDeg),
    previewZoom: clampTexturePreviewZoom(previewZoom),
    imageZoom: clampTexturePreviewZoom(imageZoom),
    imageRotationDeg: wrapTextureRotationDeg(imageRotationDeg),
    brightness: Number(brightness) || 1,
    contrast: Number(contrast) || 1,
    tintColor: String(tintColor || DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor),
    tintAmount: Math.min(1, Math.max(0, Number(tintAmount) || 0)),
});

/**
 * @deprecated используйте readImageFileAsUploadSource + модалку
 */
export const readImageFileAsTexture = async (file) => {
    const source = await readImageFileAsUploadSource(file);
    return buildCustomTextureRecord({
        id: source.id,
        name: source.suggestedName,
        url: source.sourceUrl,
        sourceUrl: source.sourceUrl,
    });
};

/** Подогнать repeat/rotation под размер детали (мм) и настройки декора. */
export const applyDetailTextureTransform = (texture, detailData, options = {}) => {
    if (!texture) return texture;

    const length = Math.max(Number(detailData?.length) || 600, 1);
    const width = Math.max(Number(detailData?.width) || 400, 1);
    const scaleMm = clampTextureScaleMm(
        options.scaleMm ?? detailData?.textureScaleMm ?? DEFAULT_TEXTURE_SCALE_MM,
    );
    const rotationDeg = wrapTextureRotationDeg(
        options.rotationDeg ?? detailData?.textureRotationDeg ?? DEFAULT_TEXTURE_ROTATION_DEG,
    );

    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.center.set(0.5, 0.5);
    texture.rotation = THREE.MathUtils.degToRad(rotationDeg);

    // При ~90°/270° оси UV меняются местами — меняем repeat, чтобы мм/тайл оставались верными.
    const snapped = ((Math.round(rotationDeg / 90) % 4) + 4) % 4;
    const swapped = snapped === 1 || snapped === 3;
    const repeatU = (swapped ? width : length) / scaleMm;
    const repeatV = (swapped ? length : width) / scaleMm;
    texture.repeat.set(repeatU, repeatV);
    texture.needsUpdate = true;
    return texture;
};

const compositionPanelTextureLoader = new THREE.TextureLoader();
compositionPanelTextureLoader.crossOrigin = "anonymous";
const compositionPanelTexturePromises = new Map();

/** Один HTTP-запрос на URL — для сцены композиции (много деталей с одной текстурой). */
export const preloadCompositionPanelTextures = (urls) => {
    if (!Array.isArray(urls)) {
        return;
    }
    [...new Set(urls.filter(Boolean))].forEach((url) => {
        if (compositionPanelTexturePromises.has(url)) {
            return;
        }
        const promise = new Promise((resolve) => {
            compositionPanelTextureLoader.load(
                url,
                (texture) => resolve(texture),
                undefined,
                () => resolve(null),
            );
        });
        compositionPanelTexturePromises.set(url, promise);
    });
};
